import { useState, useEffect, useRef } from 'react';
import { getBadgeUrl } from '../utils/badgeUrl';
import { toast } from 'react-toastify';
import { gateToast } from '../utils/toastGate';

const buildWsUrl = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
        console.error('[WS] REACT_APP_API_URL is not set');
        return null;
    }

    const url = apiUrl
        .replace(/^https/, 'wss')
        .replace(/^http/, 'ws')
        .replace(/\/api\/?$/, '/ws');

    console.log('[WS] Constructed WebSocket URL:', url);
    return url;
};

const WS_URL = buildWsUrl();
const RECONNECT_DELAY_MS = 3000;
const MAX_RECONNECT_ATTEMPTS = 10;
const STALE_HIDDEN_MS = 30 * 60 * 1000; // 30 minutes
const DEAD_SOCKET_SILENT_MS = 2 * 60 * 1000; // 2 minutes silence = probably dead

function useUnreadMessages(userData) {
    const [unreadCount, setUnreadCount] = useState(0);
    const wsRef = useRef(null);
    const reconnectAttempts = useRef(0);
    const reconnectTimer = useRef(null);
    const isMounted = useRef(true);
    const prevUnreadCount = useRef(null);
    const mailCountRef = useRef(0);
    const mailTimerRef = useRef(null);
    const unauthorizedRef = useRef(false);
    const lastMessageTimeRef = useRef(Date.now());
    const hiddenAtRef = useRef(null);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (!userData) {
            console.log('[WS] No userData, skipping WebSocket connection');
            return;
        }

        if (!WS_URL) {
            console.error('[WS] No WebSocket URL available, cannot connect');
            return;
        }

        console.log('[WS] userData present:', userData?.habboName || userData?.userId, '— initiating connection');

        const connect = () => {
            if (!isMounted.current) {
                console.log('[WS] Component unmounted, aborting connect');
                return;
            }

            if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
                console.warn('[WS] Max reconnect attempts reached, giving up');
                window.dispatchEvent(new CustomEvent('frankly:wsDisconnected'));
                return;
            }

            console.log(`[WS] Connecting to ${WS_URL} (attempt ${reconnectAttempts.current + 1})`);

            const ws = new WebSocket(WS_URL);
            wsRef.current = ws;

            ws.onopen = () => {
                console.log('[WS] Connection opened successfully');
                reconnectAttempts.current = 0;
                window.dispatchEvent(new CustomEvent('frankly:wsConnected'));
            };

            ws.onmessage = (event) => {
                console.log('[WS] Message received:', event.data);
                lastMessageTimeRef.current = Date.now();
                try {
                    const data = JSON.parse(event.data);

                    if (data.type === 'unread_count' && isMounted.current) {
                        console.log('[WS] Setting unread count to:', data.unreadCount);
                        if (prevUnreadCount.current !== null && data.unreadCount > prevUnreadCount.current) {
                            const increase = data.unreadCount - prevUnreadCount.current;
                            mailCountRef.current += increase;
                            clearTimeout(mailTimerRef.current);
                            mailTimerRef.current = setTimeout(() => {
                                if (!isMounted.current) return;
                                const count = mailCountRef.current;
                                mailCountRef.current = 0;
                                const msg = count > 1 ? `You've got mail! [${count} new]` : "You've got mail!";
                                gateToast(() => toast.mail(msg));
                                window.dispatchEvent(new CustomEvent('frankly:newMessage'));
                            }, 50);
                        }
                        prevUnreadCount.current = data.unreadCount;
                        setUnreadCount(data.unreadCount);
                    } else if (data.type === 'stamp_awarded' && isMounted.current) {
                        gateToast(() => toast.stamp(`You earned ${data.amount} framp${data.amount !== 1 ? 's' : ''}!`));
                    } else if (data.type === 'achievement_awarded' && isMounted.current) {
                        gateToast(() => toast.achievement(data.achievementName, {
                            title: 'Achievement Unlocked!',
                            iconSrc: getBadgeUrl(data.badgeID, data.uploaded),
                        }));
                    } else if (data.type === 'feed_updated' && isMounted.current) {
                        window.dispatchEvent(new CustomEvent('frankly:feedUpdated'));
                    } else if (data.type === 'status_posted' && isMounted.current) {
                        window.dispatchEvent(new CustomEvent('frankly:statusPosted', { detail: data }));
                    } else if (data.type === 'cloud_upload_mode' && isMounted.current) {
                        window.dispatchEvent(new CustomEvent('frankly:cloudUploadMode', { detail: { mode: data.mode } }));
                    } else if (data.type === 'cloud_image_deleted' && isMounted.current) {
                        window.dispatchEvent(new CustomEvent('frankly:cloudImageDeleted', { detail: { itemId: data.itemId } }));
                    } else if (data.type === 'easter_available' && isMounted.current) {
                        window.dispatchEvent(new CustomEvent('frankly:easter-available', { detail: data }));
                    } else if (data.type === 'easter_hidden' && isMounted.current) {
                        window.dispatchEvent(new CustomEvent('frankly:easter-hidden'));
                    } else {
                        console.warn('[WS] Received unknown message type:', data.type);
                    }
                } catch (err) {
                    console.error('[WS] Failed to parse message:', err, '| Raw:', event.data);
                }
            };

            ws.onclose = (event) => {
                console.log(`[WS] Connection closed — code: ${event.code}, reason: "${event.reason}", clean: ${event.wasClean}`);

                if (event.code === 4001) {
                    console.error('[WS] Unauthorised (4001) — JWT cookie missing or expired, not retrying');
                    unauthorizedRef.current = true;
                    window.dispatchEvent(new CustomEvent('frankly:wsDisconnected'));
                    return;
                }

                if (!isMounted.current) return;

                reconnectAttempts.current += 1;
                console.log(`[WS] Scheduling reconnect in ${RECONNECT_DELAY_MS}ms`);
                reconnectTimer.current = setTimeout(connect, RECONNECT_DELAY_MS);
            };

            ws.onerror = (err) => {
                console.error('[WS] Socket error:', err);
                ws.close();
            };
        };

        connect();

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                hiddenAtRef.current = Date.now();
                return;
            }

            // Tab is visible again
            if (hiddenAtRef.current !== null) {
                const hiddenDuration = Date.now() - hiddenAtRef.current;
                hiddenAtRef.current = null;
                if (hiddenDuration >= STALE_HIDDEN_MS) {
                    console.log('[WS] Tab was hidden for >30 min — reloading for fresh content');
                    window.location.reload();
                    return;
                }
            }

            if (unauthorizedRef.current) return;

            const ws = wsRef.current;
            if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
                console.log('[WS] Tab became visible, socket is dead — reconnecting');
                reconnectAttempts.current = 0;
                clearTimeout(reconnectTimer.current);
                connect();
            } else if (ws.readyState === WebSocket.OPEN) {
                // Socket claims open but may be silently dead — reconnect if no message in a while
                const silentMs = Date.now() - lastMessageTimeRef.current;
                if (silentMs > DEAD_SOCKET_SILENT_MS) {
                    console.log(`[WS] Socket open but silent for ${Math.round(silentMs / 1000)}s — forcing reconnect`);
                    ws.onclose = null;
                    ws.close();
                    reconnectAttempts.current = 0;
                    clearTimeout(reconnectTimer.current);
                    connect();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            console.log('[WS] Cleaning up — closing socket');
            isMounted.current = false;
            clearTimeout(reconnectTimer.current);
            clearTimeout(mailTimerRef.current);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (wsRef.current) {
                wsRef.current.onclose = null;
                wsRef.current.close();
            }
        };
    }, [userData]);

    return unreadCount;
}

export default useUnreadMessages;